/**
 * Helper to resolve SEO metadata for any route or path
 */
export const resolveRouteSEO = (to = {}, settings = {}) => {
    const path = typeof to === 'string' ? to : (to.path || (typeof window !== 'undefined' ? window.location.pathname : '/'))
    let pageSeo = null
    const staticPages = settings.seoStaticPages || []
    if (Array.isArray(staticPages)) {
        const aliasMap = {
            '/terms-of-service': ['/terms-of-service', '/terms', '/terms-of-use'],
            '/terms': ['/terms', '/terms-of-service', '/terms-of-use'],
            '/warranty-policy': ['/warranty-policy', '/warranty'],
            '/warranty': ['/warranty', '/warranty-policy'],
            '/payment-guide': ['/payment-guide', '/payment-methods'],
            '/payment-methods': ['/payment-methods', '/payment-guide']
        }
        const candidates = aliasMap[path] || [path]
        pageSeo = staticPages.find(p => candidates.includes(p.path))
    }

    const siteName = settings.storeName || 'Outdoor Lander'
    const globalTitle = settings.seoGlobalTitle || `${siteName} — บ้านเก็บของสำเร็จรูป โรงเรือน โกดังเก็บของ คุณภาพพรีเมียม`
    const globalDesc = settings.seoGlobalDescription || 'ผู้นำด้านบ้านเก็บของสำเร็จรูป โรงเรือน โกดังเก็บของ'
    const globalKeywords = settings.seoGlobalKeywords || 'บ้านเก็บของ, โรงเรือน, โกดังเก็บของ, บ้านเก็บของสำเร็จรูป, Outdoor Lander'
    const globalOgImage = settings.seoGlobalOgImage || '/og-image.jpg'

    let resolvedTitle = ''
    let resolvedDesc = ''
    let resolvedKeywords = ''

    if (path === '/') {
        // Home page: Prioritize static page custom SEO -> Global SEO Settings -> Route Meta -> siteName
        resolvedTitle = (pageSeo && pageSeo.title) ? pageSeo.title : (globalTitle || to.meta?.title || siteName)
        resolvedDesc = (pageSeo && pageSeo.description) ? pageSeo.description : (globalDesc || to.meta?.description || '')
        resolvedKeywords = (pageSeo && pageSeo.keywords) ? pageSeo.keywords : (globalKeywords || to.meta?.keywords || '')
    } else {
        // Other pages: Prioritize static page custom SEO -> Route Meta -> Global fallback
        resolvedTitle = (pageSeo && pageSeo.title)
            ? pageSeo.title
            : (to.meta?.title ? (siteName && !to.meta.title.includes(siteName) ? `${to.meta.title} - ${siteName}` : to.meta.title) : globalTitle)
        resolvedDesc = (pageSeo && pageSeo.description) ? pageSeo.description : (to.meta?.description || globalDesc)
        resolvedKeywords = (pageSeo && pageSeo.keywords) ? pageSeo.keywords : (to.meta?.keywords || globalKeywords)
    }

    const formattedOgImage = globalOgImage.startsWith('http://') || globalOgImage.startsWith('https://')
        ? globalOgImage
        : `https://outdoorlander.com${globalOgImage.startsWith('/') ? '' : '/'}${globalOgImage}`

    return {
        title: resolvedTitle,
        description: resolvedDesc,
        keywords: resolvedKeywords,
        ogImage: formattedOgImage,
        canonical: `https://outdoorlander.com${path === '/' ? '' : path}`
    }
}

/**
 * Helper to apply resolved SEO metadata to the active DOM
 */
export const applyDOMMetaTags = (seoData, fullPath = '') => {
    if (typeof document === 'undefined' || !seoData) return

    const currentPath = fullPath || (typeof window !== 'undefined' ? window.location.pathname : '/')

    // Set Title
    if (seoData.title) {
        document.title = seoData.title
    }

    // Set Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', seoData.canonical || `https://outdoorlander.com${currentPath === '/' ? '' : currentPath}`)

    // Set Meta Description
    const metaDescriptionTag = document.querySelector('meta[name="description"]')
    if (metaDescriptionTag) {
        metaDescriptionTag.setAttribute('content', seoData.description || '')
    } else {
        const meta = document.createElement('meta')
        meta.name = 'description'
        meta.content = seoData.description || ''
        document.head.appendChild(meta)
    }

    // Set Meta Keywords
    let metaKeywordsTag = document.querySelector('meta[name="keywords"]')
    if (!metaKeywordsTag) {
        metaKeywordsTag = document.createElement('meta')
        metaKeywordsTag.name = 'keywords'
        document.head.appendChild(metaKeywordsTag)
    }
    metaKeywordsTag.setAttribute('content', seoData.keywords || '')

    // Dynamic OG & Twitter Meta Tags for social sharing
    const ogTags = {
        'og:title': seoData.title,
        'og:description': seoData.description,
        'og:url': `https://outdoorlander.com${currentPath}`,
        'og:image': seoData.ogImage,
        'twitter:title': seoData.title,
        'twitter:description': seoData.description,
        'twitter:image': seoData.ogImage,
    }
    for (const [property, content] of Object.entries(ogTags)) {
        if (!content) continue
        const isOg = property.startsWith('og:')
        const selector = isOg ? `meta[property="${property}"]` : `meta[name="${property}"]`
        let tag = document.querySelector(selector)
        if (tag) {
            tag.setAttribute('content', content)
        } else {
            tag = document.createElement('meta')
            if (isOg) tag.setAttribute('property', property)
            else tag.name = property
            tag.content = content
            document.head.appendChild(tag)
        }
    }
}
